import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Button } from "../ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdr = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const KIND_DOT = { success: "bg-emerald-500", warning: "bg-amber-500", error: "bg-red-500", info: "bg-blue-500" };

export const NotificationsBell = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/commissions/notifications`, hdr());
      setData(r.data || { items: [], unread: 0 });
    } catch (_e) { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const onOpen = async (open) => {
    if (!open || !data.unread) return;
    try {
      await axios.post(`${API}/commissions/notifications/mark-read`, {}, hdr());
      setData((d) => ({ ...d, unread: 0 }));
    } catch (_e) { /* silent */ }
  };

  return (
    <DropdownMenu onOpenChange={onOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" data-testid="notifications-button"
          className="relative h-8 w-8 text-white/70 hover:bg-white/10 hover:text-white">
          <Bell className="h-4 w-4" />
          {data.unread > 0 && <span data-testid="notifications-unread-dot" className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-red-500" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80" data-testid="notifications-menu">
        <DropdownMenuLabel>Notifications</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {data.items.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">No new notifications</div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {data.items.map((n) => (
              <button key={n.id} type="button" data-testid={`notification-item-${n.id}`}
                onClick={() => n.link && navigate(n.link)}
                className="flex w-full gap-2 px-3 py-2 text-left hover:bg-slate-50">
                <span className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${KIND_DOT[n.kind] || KIND_DOT.info}`} />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-slate-800">{n.title}</span>
                  <span className="block text-xs text-slate-600">{n.message}</span>
                  <span className="block text-[10px] text-slate-400">{new Date(n.created_at).toLocaleString("en-IN")}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
